import React, { useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { Badge } from '@prisjakt/ui/badge';
import { IconVerfied, IconHomeDelivery } from '@prisjakt/ui/icon';
import { FontEmphasis } from '@prisjakt/ui/text';
import Tooltip from '@prisjakt/ui-tooltip-new';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import CampaignBadge from 'src/components/campaign-badge';
import { ShippingLabel, PriceLabel } from '../price-column';
import StockInfo from '../stock-info';
import PriceTypeNotice from '../stock-info/price-type-notice';
import { DeliveryTime } from '../delivery-time';
import { getDeliveryDays, ShippingType } from '../delivery-time/utils';
import { IStockPriceInfo } from '../../types';
import * as S from './styles';

const Label: React.FC = props => {
  return (
    <S.StyledLabel emphasis={FontEmphasis.MEDIUM}>
      {props.children}
    </S.StyledLabel>
  );
};

const StockPriceInfo: React.FC<IStockPriceInfo> = props => {
  const {
    featured,
    campaign,
    price,
    priceDisplay,
    priceType,
    stock,
    isMemberPrice,
    countryCode,
    isInternationalPrice = false,
    deliveryOptions = [],
    brandRecommendedRetailer,
    shipping,
  } = props;

  const { t } = useContext(AppContext);

  const homeDelivery =
    deliveryOptions &&
    deliveryOptions.find(option => option && option.type === 'HOME_DELIVERY');

  const showDeliveryTime =
    shipping && Boolean(getDeliveryDays(shipping, ShippingType.CHEAPEST));

  return (
    <S.Column>
      <S.Row>
        {price && featured && (
          <ShippingLabel price={price} priceDisplay={priceDisplay} />
        )}

        {campaign && <CampaignBadge colors={campaign.media.colors} />}

        {featured && isMemberPrice && (
          <Badge color="GREY_INVERTED" text="Members" />
        )}

        {price && (
          <PriceLabel
            price={price}
            priceDisplay={priceDisplay}
            storeIsFeatured={featured}
            isCampaign={Boolean(campaign)}
          />
        )}
      </S.Row>
      {featured && (
        <S.Row>
          {brandRecommendedRetailer && (
            <S.StoreIcon>
              <Tooltip
                placement="top"
                title={t('store', 'brand.recommended-retailer')}
                renderOnMouseOver
              >
                <IconVerfied color="base300" size={18} />
              </Tooltip>
              <S.DesktopOnly>
                <Label>{t('store', 'brand.recommended-retailer-short')}</Label>
              </S.DesktopOnly>
            </S.StoreIcon>
          )}

          {priceType && <PriceTypeNotice priceType={priceType} />}

          {homeDelivery && (
            <S.StoreIcon>
              <Tooltip
                placement="top"
                title={t(
                  'product',
                  `home-delivery.info` as keyof ParamsAndReturnTypes['product']
                )}
                renderOnMouseOver
              >
                <IconHomeDelivery color="base300" size={18} />
              </Tooltip>
            </S.StoreIcon>
          )}

          {countryCode && isInternationalPrice && (
            <S.CountryFlag
              show={isInternationalPrice}
              countryCode={countryCode}
              title={t(
                'common',
                `market.${countryCode}` as keyof ParamsAndReturnTypes['common']
              )}
              tooltipPlacement="top"
            />
          )}

          <StockInfo
            status={stock && stock.status}
            statusText={stock && stock.statusText}
            toneDown={!featured}
            showLabel={!showDeliveryTime}
          />

          {shipping && showDeliveryTime && <DeliveryTime shipping={shipping} />}
        </S.Row>
      )}
    </S.Column>
  );
};

export default StockPriceInfo;
